import React, { useState } from 'react';
import {
    Box, AppBar, Toolbar, Typography, IconButton, Button,
    Menu, MenuItem, Tooltip, LinearProgress, Stack, FormControl,
    InputLabel, Select, Checkbox, FormControlLabel, Autocomplete, TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import TranslateIcon from '@mui/icons-material/Translate';
import SaveIcon from '@mui/icons-material/Save';
import { CircleFlag } from 'react-circle-flags';

// Maps for language codes to display names/flags (simplified for this component)
// Ideally this comes from a shared utility or props if dynamic
const getLanguageDetails = (code) => {
    // Mock or passed down? 
    // For now, let's assume the parent passes resolved names/codes or we use a basic map.
    // Actually, looking at the parent, it uses a helper. 
    // We'll trust the parent passes "sourceLanguage" and "targetLanguage" which are the codes.
    // We might need the full objects or a helper.
    // Let's rely on props for the *names* and *codes* to be safe.
    return { name: code, countryCode: code }; // Fallback
};

const TranslationHeader = ({
    projectName,
    sourceLanguage,
    targetLanguage,
    // Methods to get details if needed, or pass pre-resolved strings:
    sourceLangDetails, // { name: 'English', countryCode: 'us' }
    targetLangDetails, // { name: 'Spanish', countryCode: 'es' }

    wordCount,
    charCount,
    overallProgress,

    isAutoTranslating,
    autoTranslateProgress,
    isSaving,
    isProjectOwner,

    useTranslationMemory,
    onToggleTranslationMemory,

    onBack,
    onSave, // Auto-save usually, but maybe a manual trigger? (Parent has auto-save)
    onAutoTranslate,
    onQA,

    // Filters
    filterMode,
    setFilterMode,
    filterEmptyTranslations,
    setFilterEmptyTranslations,

    // Versioning
    versionHistory,
    selectedVersion,
    onChangeVersion,
    pageFilter,
    setPageFilter,
    availablePages
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openSettings = Boolean(anchorEl);

    const handleOpenSettings = (event) => setAnchorEl(event.currentTarget);
    const handleCloseSettings = () => setAnchorEl(null);

    return (
        <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: 'white' }}>
            <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>

                {/* LEFT: Navigation & Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                    <IconButton edge="start" onClick={onBack}>
                        <ArrowBackIcon />
                    </IconButton>

                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" noWrap sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                {projectName}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                            {/* Language Pair */}
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CircleFlag countryCode={(sourceLangDetails?.countryCode || 'us').toLowerCase()} height="14" />
                                <Typography variant="caption">{sourceLangDetails?.name || sourceLanguage}</Typography>
                                <Typography variant="caption">→</Typography>
                                <CircleFlag countryCode={(targetLangDetails?.countryCode || 'es').toLowerCase()} height="14" />
                                <Typography variant="caption">{targetLangDetails?.name || targetLanguage}</Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Box>

                {/* CENTER: Status & Progress */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 400 }}>
                    {isAutoTranslating ? (
                        <Box sx={{ width: '100%' }}>
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption" color="primary" fontWeight={600}>
                                    Auto-Translating...
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {Math.round(autoTranslateProgress)}%
                                </Typography>
                            </Stack>
                            <LinearProgress variant="determinate" value={autoTranslateProgress} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : (
                        <Box sx={{ width: '100%' }}>
                            {/* Single Progress Bar with Summary */}
                            <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption" fontWeight={600} color="text.primary">
                                    {Math.round(overallProgress)}% Complete
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {wordCount.toLocaleString()} words
                                </Typography>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={overallProgress}
                                color={overallProgress === 100 ? "success" : "primary"}
                                sx={{ borderRadius: 1, height: 6 }}
                            />
                        </Box>
                    )}
                </Box>

                {/* RIGHT: Actions & Settings */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>

                    {/* Primary Actions */}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SpellcheckIcon />}
                        onClick={onQA}
                    >
                        QA Check
                    </Button>


                    {/* Settings Dropdown */}
                    <Tooltip title="View Settings">
                        <IconButton onClick={handleOpenSettings} color={openSettings ? 'primary' : 'default'}>
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>

                    <Menu
                        anchorEl={anchorEl}
                        open={openSettings}
                        onClose={handleCloseSettings}
                        PaperProps={{ sx: { width: 300, p: 2 } }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                            View Settings
                        </Typography>

                        {/* Filters */}
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Filter Segments</InputLabel>
                            <Select
                                value={filterMode}
                                label="Filter Segments"
                                onChange={(e) => setFilterMode(e.target.value)}
                            >
                                <MenuItem value="all">All Segments</MenuItem>
                                <MenuItem value="untranslated">Untranslated</MenuItem>
                                <MenuItem value="unapproved">Unapproved</MenuItem>
                                <MenuItem value="flagged">Flagged / Annotated</MenuItem>
                            </Select>
                        </FormControl>

                        {availablePages && availablePages.length > 0 && (
                            <Autocomplete
                                size="small"
                                options={['all', ...availablePages]}
                                getOptionLabel={(option) => option === 'all' ? 'All Pages' : `Page ${option}`}
                                value={pageFilter}
                                onChange={(event, newValue) => {
                                    setPageFilter(newValue || 'all');
                                }}
                                renderInput={(params) => <TextField {...params} label="Filter by Page" />}
                                sx={{ mb: 2 }}
                                disableClearable
                            />
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filterEmptyTranslations}
                                    onChange={(e) => setFilterEmptyTranslations(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={<Typography variant="body2">Show Empty Only</Typography>}
                        />

                        {/* Translation Memory */}
                        {isProjectOwner && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useTranslationMemory}
                                        onChange={onToggleTranslationMemory}
                                        size="small"
                                    />
                                }
                                label={<Typography variant="body2">Use Translation Memory</Typography>}
                            />
                        )}


                        {/* Version History */}
                        <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                            Version History
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={selectedVersion !== undefined ? selectedVersion : ""}
                                displayEmpty
                                onChange={onChangeVersion}
                            >
                                <MenuItem value="">Original (Current)</MenuItem>
                                {versionHistory.map((v, i) => (
                                    <MenuItem key={i} value={i}>
                                        {new Date(v.updatedAt).toLocaleString()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Menu>
                </Box>

            </Toolbar>
        </AppBar>
    );
};

export default TranslationHeader;
